// @ts-nocheck

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, collectionGroup, query, where, orderBy, limit, startAfter, getDocs, DocumentSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { MediaItem, MediaFile, getThumbnail } from '../types';
import { formatPriceWithShipping, fetchAndStoreForexRates } from '../utils/price.utils';
import {
    Search,
    Eye,
    ShoppingCart,
    TrendingUp,
    Calendar,
    ArrowUpDown,
    Filter,
    ChevronLeft,
    ChevronRight,
    Package,
    ExternalLink,
    CheckCircle,
    Clock,
    Layers,
    Video,
    X,
    Play,
    Store
} from 'lucide-react';

const daysAgo = (timestamp: any): string => {
    if (!timestamp) return '';
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const diff = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'today';
    if (diff === 1) return '1 day ago';
    return `${diff} days ago`;
};

const TrendingPage: React.FC = () => {
    const [products, setProducts] = useState<MediaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<'createdAt' | 'views'>('views');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSnapshots, setPageSnapshots] = useState<{ [page: number]: DocumentSnapshot }>({});
    const [hasMore, setHasMore] = useState(false);
    const [publishedFilter, setPublishedFilter] = useState<'published' | 'unpublished' | 'all'>('unpublished');
    const [timeRange, setTimeRange] = useState<'30' | '60' | '90' | 'all'>('all');
    const { currentUser, isAdmin } = useAuth();
    const [previewProduct, setPreviewProduct] = useState<MediaItem | null>(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);

    const loadProducts = useCallback(async (page: number = 1) => {
        if (!currentUser) return;

        try {
            setLoading(true);
            setError(null);

            // Build base query
            let q = collectionGroup(db, 'products');

            // Apply published filter
            if (publishedFilter !== 'all') {
                q = query(q, where('isPublished', '==', publishedFilter === 'published'));
            }

            // Apply search filter if query exists
            if (searchQuery.trim()) {
                // Note: Firestore doesn't support full-text search natively
                // This is a simple name-based filter
                q = query(q, where('name', '>=', searchQuery), where('name', '<=', searchQuery + '\uf8ff'));
            }

            // Apply time range filter (using >= and <= for date range)
            if (timeRange !== 'all') {
                const daysAgo = parseInt(timeRange);
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - daysAgo);
                const endDate = new Date(); // now
                q = query(q,
                    where('createdAt', '>=', startDate),
                    where('createdAt', '<=', endDate)
                );
            }

            // Always sort by views first, then createdAt
            if (sortBy === 'views') {
                q = query(q, orderBy('stats.total.views', 'desc'), orderBy('createdAt', 'desc'));
            } else {
                q = query(q, orderBy('createdAt', 'desc'));
            }

            // Apply pagination using stored snapshots
            if (page > 1 && pageSnapshots[page - 1]) {
                q = query(q, startAfter(pageSnapshots[page - 1]));
            }

            q = query(q, limit(50));

            const snapshot = await getDocs(q);
            const fetchedProducts: MediaItem[] = [];

            snapshot.forEach((doc) => {
                if (doc.data().redirectTo) return;
                fetchedProducts.push({
                    id: doc.id,
                    $key: doc.id,
                    ...doc.data()
                } as MediaItem);
            });

            // Always replace products (pagination, not infinite scroll)
            setProducts(fetchedProducts);
            setCurrentPage(page);

            // Store the last document for this page
            if (snapshot.docs.length > 0) {
                setPageSnapshots(prev => ({
                    ...prev,
                    [page]: snapshot.docs[snapshot.docs.length - 1]
                }));
            }

            setHasMore(snapshot.docs.length === 50);

        } catch (err) {
            console.error('Error fetching products:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }, [currentUser, publishedFilter, searchQuery, sortBy, timeRange, pageSnapshots]);

    useEffect(() => {
        fetchAndStoreForexRates().then(() => {
            loadProducts();
        });
    }, []);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setPageSnapshots({});
        loadProducts(1);
    };

    const handlePublishedFilterChange = (filter: 'published' | 'unpublished' | 'all') => {
        setPublishedFilter(filter);
        setCurrentPage(1);
        setPageSnapshots({});
        loadProducts(1);
    };

    const handleSortChange = (sort: 'createdAt' | 'views') => {
        setSortBy(sort);
        setCurrentPage(1);
        setPageSnapshots({});
        loadProducts(1);
    };

    const handleTimeRangeChange = (range: '30' | '60' | '90' | 'all') => {
        setTimeRange(range);
        setCurrentPage(1);
        setPageSnapshots({});
        loadProducts(1);
    };

    const goToNextPage = () => {
        if (!loading && hasMore) {
            loadProducts(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (!loading && currentPage > 1) {
            loadProducts(currentPage - 1);
        }
    };

    const hasVideo = (product: MediaItem) => {
        return product.media?.some(m => m.type === 'video');
    };

    const openPreview = (e: React.MouseEvent, product: MediaItem) => {
        e.preventDefault();
        e.stopPropagation();
        setPreviewProduct(product);
        setPreviewIndex(0);
    };

    const closePreview = () => {
        setPreviewProduct(null);
        setPreviewIndex(0);
    };

    const getMediaUrl = (media: MediaFile): string => {
        const raw = media.downloadURL;
        if (!raw) return '';
        if (raw.includes('instagram') || raw.includes('cdninstagram') || !raw.includes('sellox')) {
            return raw;
        }
        try {
            let path = new URL(raw).pathname;
            path = path.replace(/.*.com/, '');
            path = path.substring(3);
            if (media.type === 'video') {
                return raw;
            }
            return 'https://cdn.sellox.app/preview/' + path + '?auto=compress&width=800&height=800&fit=contain';
        } catch {
            return raw;
        }
    };


    if (loading && products.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    {/* Search Row */}
                    <form onSubmit={handleSearch} className="flex gap-3">
                        <div className="flex-1 relative">
                            <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search products..."
                                className="w-full h-11 pl-12 pr-4 bg-gray-100 border-0 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all"
                            />
                        </div>
                        <button
                            type="submit"
                            className="h-11 px-5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
                        >
                            <Search className="w-4 h-4" />
                            <span className="hidden sm:inline">Search</span>
                        </button>
                    </form>

                    {/* Filters Row */}
                    <div className="flex flex-wrap items-center gap-3 mt-4">
                        {/* Time Range */}
                        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                            <Calendar className="w-4 h-4 text-gray-500 ml-2" />
                            <select
                                value={timeRange}
                                onChange={(e) => handleTimeRangeChange(e.target.value as '30' | '60' | '90' | 'all')}
                                className="h-8 pl-1 pr-8 bg-transparent border-0 text-sm text-gray-700 focus:ring-0 cursor-pointer"
                            >
                                <option value="all">All Time</option>
                                <option value="30">Last 30 Days</option>
                                <option value="60">Last 60 Days</option>
                                <option value="90">Last 90 Days</option>
                            </select>
                        </div>

                        {/* Divider */}
                        <div className="hidden sm:block w-px h-6 bg-gray-300" />

                        {/* Sort By */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <ArrowUpDown className="w-4 h-4 text-gray-500 ml-2" />
                            <button
                                type="button"
                                onClick={() => handleSortChange('views')}
                                className={`h-8 px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${sortBy === 'views'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                <Eye className="w-3.5 h-3.5" />
                                Views
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSortChange('createdAt')}
                                className={`h-8 px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${sortBy === 'createdAt'
                                    ? 'bg-white text-purple-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                <Clock className="w-3.5 h-3.5" />
                                Newest
                            </button>
                        </div>

                        {/* Status Filter (Admin only) */}
                        {isAdmin && (
                            <>
                                <div className="hidden sm:block w-px h-6 bg-gray-300" />
                                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                                    <Filter className="w-4 h-4 text-gray-500 ml-2" />
                                    <button
                                        type="button"
                                        onClick={() => handlePublishedFilterChange('published')}
                                        className={`h-8 px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${publishedFilter === 'published'
                                            ? 'bg-white text-green-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        Live
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handlePublishedFilterChange('unpublished')}
                                        className={`h-8 px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${publishedFilter === 'unpublished'
                                            ? 'bg-white text-amber-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        <Clock className="w-3.5 h-3.5" />
                                        Draft
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handlePublishedFilterChange('all')}
                                        className={`h-8 px-3 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${publishedFilter === 'all'
                                            ? 'bg-white text-purple-600 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        <Layers className="w-3.5 h-3.5" />
                                        All
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Package className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <p className="font-medium text-red-800">Error loading products</p>
                            <p className="text-sm text-red-600 mt-0.5">{error}</p>
                            <button
                                onClick={() => loadProducts(1)}
                                className="mt-2 text-sm font-medium text-red-700 hover:text-red-900"
                            >
                                Try again →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Products Grid */}
            <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
                {products.length === 0 && !loading ? (
                    <div className="text-center py-20">
                        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Package className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No products found</h3>
                        <p className="text-gray-500 text-sm">Try a different search term or adjust your filters.</p>
                    </div>
                ) : (
                    <>
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600"></div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                                    {products.map((product) => (
                                        <div
                                            key={product.id}
                                            className="group cursor-pointer"
                                            onClick={(e) => openPreview(e, product)}
                                        >
                                            <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all duration-200">
                                                {/* Product Image */}
                                                <div className="relative aspect-square bg-gray-100">
                                                    <img
                                                        src={getThumbnail(product, 'grid')}
                                                        alt={product.name || 'Product'}
                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                        loading="lazy"
                                                    />

                                                    {/* Status badge */}
                                                    <div className="absolute top-2 left-2 flex items-center gap-1.5">
                                                        {product.isPublished ? (
                                                            <span className="inline-flex items-center gap-1 bg-green-500/90 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md font-medium">
                                                                <CheckCircle className="w-3 h-3" />
                                                                Live
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1 bg-amber-500/90 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md font-medium">
                                                                <Clock className="w-3 h-3" />
                                                                Draft
                                                            </span>
                                                        )}
                                                        {hasVideo(product) && (
                                                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                                                                <span className="inline-flex items-center justify-center w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full shadow-md">
                                                                    <Play className="w-5 h-5 text-gray-800 ml-0.5" />
                                                                </span>
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* External link indicator */}

                                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <a
                                                            href={`https://looksbysellox.com/shops/${product.shop.$key}/product/${product.$key}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <span className="inline-flex items-center justify-center w-7 h-7 bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
                                                                <ExternalLink className="w-3.5 h-3.5 text-gray-700" />
                                                            </span>
                                                        </a>
                                                    </div>
                                                </div>

                                                {/* Product Info */}
                                                <div className="p-3">
                                                    {product.name && (
                                                        <h3 className="font-medium text-gray-900 text-sm mb-1.5 line-clamp-2 leading-snug">
                                                            {(product.name?.split('/')[0] || '').trim()}
                                                        </h3>
                                                    )}

                                                    {/* Price */}
                                                    {product.hasActiveOffer ? (
                                                        <div className="flex items-center gap-2 flex-wrap mb-2">
                                                            <span className="text-gray-400 line-through text-xs">
                                                                {formatPriceWithShipping(product.price?.value, product.shop?.currency?.value)}
                                                            </span>
                                                            <span className="text-purple-600 font-semibold text-sm">
                                                                {formatPriceWithShipping(
                                                                    parseFloat(product.price?.value || '0') * (1 - (product.activeOffers?.[0]?.amount || 0) / 100),
                                                                    product.shop?.currency?.value
                                                                )}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        product.price?.value && (
                                                            <p className="text-purple-600 font-semibold text-sm mb-2">
                                                                {formatPriceWithShipping(product.price.value, product.shop?.currency?.value)}
                                                            </p>
                                                        )
                                                    )}

                                                    {/* Shop & Date */}
                                                    <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5">
                                                        {product.shop?.businessName && (
                                                            <span className="truncate">{product.shop.businessName}</span>
                                                        )}
                                                        {product.createdAt && (
                                                            <span className="whitespace-nowrap ml-auto">{daysAgo(product.createdAt)}</span>
                                                        )}
                                                    </div>

                                                    {/* Stats */}
                                                    <div className="flex items-center gap-3 text-xs text-gray-500">
                                                        <span className="inline-flex items-center gap-1" title="Weekly Views">
                                                            <Eye className="w-3.5 h-3.5" />
                                                            {product.stats?.total?.views || 0}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1" title="Weekly Add to Cart">
                                                            <ShoppingCart className="w-3.5 h-3.5" />
                                                            {product.stats?.total?.addCartCount || 0}
                                                        </span>
                                                        <span className="inline-flex items-center gap-1" title="Conversion Rate">
                                                            <TrendingUp className="w-3.5 h-3.5" />
                                                            {product.stats?.total?.views ? ((product.stats?.monthly?.addCartCount || 0) / product.stats.monthly.views * 100).toFixed(1) : 0}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination Controls */}
                                {(currentPage > 1 || hasMore) && (
                                    <div className="flex justify-center items-center gap-2 mt-8 mb-8">
                                        <button
                                            onClick={goToPreviousPage}
                                            disabled={currentPage === 1 || loading}
                                            className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </button>
                                        <span className="inline-flex items-center justify-center h-10 px-4 bg-purple-50 text-purple-700 font-semibold rounded-lg min-w-[80px]">
                                            Page {currentPage}
                                        </span>
                                        <button
                                            onClick={goToNextPage}
                                            disabled={!hasMore || loading}
                                            className="inline-flex items-center gap-2 h-10 px-4 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            {/* Media Preview Modal */}
            {previewProduct && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={closePreview}
                >
                    <div
                        className="relative bg-white rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                            <div className="flex items-center gap-3 min-w-0">
                                <h3 className="font-semibold text-gray-900 text-sm truncate">
                                    {(previewProduct.name?.split('/')[0] || 'Product').trim()}
                                </h3>
                                {previewProduct.media && previewProduct.media.length > 1 && (
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                        {previewIndex + 1} / {previewProduct.media.length}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {previewProduct.shop?.username && (
                                    <a
                                        href={`https://business.sellox.app/?impersonate=${previewProduct.shop.username}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-800 px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
                                    >
                                        <Store className="w-3.5 h-3.5" />
                                        Go to Shop
                                    </a>
                                )}
                                <a
                                    href={`https://business.sellox.app/shop/edit/${previewProduct.$key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-800 px-3 py-1.5 rounded-lg hover:bg-purple-50 transition-colors"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                    Edit
                                </a>
                                <button
                                    onClick={closePreview}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Media Area */}
                        <div className="relative bg-gray-100 flex items-center justify-center" style={{ minHeight: '400px', maxHeight: '70vh' }}>
                            {previewProduct.media && previewProduct.media.length > 0 ? (
                                <>
                                    {previewProduct.media[previewIndex]?.type === 'video' ? (
                                        <video
                                            ref={videoRef}
                                            key={previewIndex}
                                            src={getMediaUrl(previewProduct.media[previewIndex])}
                                            controls
                                            autoPlay
                                            className="w-full h-full object-contain"
                                            style={{ maxHeight: '70vh' }}
                                            playsInline
                                        />
                                    ) : (
                                        <img
                                            src={getMediaUrl(previewProduct.media[previewIndex])}
                                            alt={previewProduct.name || 'Product'}
                                            className="w-full h-full object-contain"
                                            style={{ maxHeight: '70vh' }}
                                        />
                                    )}

                                    {/* Navigation arrows */}
                                    {previewProduct.media.length > 1 && (
                                        <>
                                            <button
                                                onClick={() => setPreviewIndex((prev) => (prev - 1 + previewProduct.media.length) % previewProduct.media.length)}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
                                            >
                                                <ChevronLeft className="w-5 h-5 text-gray-700" />
                                            </button>
                                            <button
                                                onClick={() => setPreviewIndex((prev) => (prev + 1) % previewProduct.media.length)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
                                            >
                                                <ChevronRight className="w-5 h-5 text-gray-700" />
                                            </button>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="text-gray-400 text-center py-20">
                                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">No media available</p>
                                </div>
                            )}
                        </div>

                        {/* Thumbnail strip */}
                        {previewProduct.media && previewProduct.media.length > 1 && (
                            <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-200 overflow-x-auto">
                                {previewProduct.media.map((m, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setPreviewIndex(idx)}
                                        className={`relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${idx === previewIndex
                                            ? 'border-purple-500 ring-2 ring-purple-200'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        {m.type === 'video' ? (
                                            <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                                                <Play className="w-5 h-5 text-white" />
                                            </div>
                                        ) : (
                                            <img
                                                src={getMediaUrl(m)}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrendingPage;
